/* ============================================================
   High Rise — Bottles PDP state machine.
   - Flavor swap: citrus / orange / raspberry tiles
   - Strength toggle: 5mg / 10mg radios (citrus only has both)
   - Gallery thumbnail swap
   - Purchase mode: one-time vs subscribe
   - Quantity stepper
   - ATC → Shopify /cart/add.js with the right variant_id +
     selling_plan_id from window.HR_BOTTLE_DATA (rendered live
     from Liquid on every page load).
   ============================================================ */
(() => {
  'use strict';

  const root = document.querySelector('[data-hr-landing][data-hr-page="bottles"]');
  if (!root) return;

  const DATA = window.HR_BOTTLE_DATA || {};
  // Deep-link support: `?p=orange` or `?p=raspberry` selects that
  // flavor on load. Liquid can't read query params in /pages/ context
  // (request.url is nil), so we resolve it client-side.
  const URL_FLAVORS = ['citrus', 'orange', 'raspberry'];
  const urlFlavor = new URLSearchParams(location.search).get('p');
  let currentFlavor = (URL_FLAVORS.includes(urlFlavor) ? urlFlavor : null)
                   || window.HR_BOTTLE_DEFAULT_FLAVOR
                   || 'citrus';
  let currentStrength = '10';   // default, narrowed below per flavor

  const $  = (s, el = document) => el.querySelector(s);
  const $$ = (s, el = document) => [...el.querySelectorAll(s)];
  const setText = (id, v) => { const e = document.getElementById(id); if (e) e.textContent = v; };
  const setHTML = (id, v) => { const e = document.getElementById(id); if (e) e.innerHTML = v; };

  /* ---- Static flavor content (copy + reviews + image set) ----
     Prices come from Shopify (HR_BOTTLE_DATA), but the marketing
     copy, taglines, and review text stay ours per the brief. */
  const COPY = {
    citrus: {
      title: 'Citrus',
      eyebrow: 'Botanical Infusion · 750mL',
      tagline: 'Bright, balanced, lime-forward — 15 servings of 50mL pours per bottle.',
      tagline2: 'Lime-forward, vibrant, finishes smooth and creamy.',
      pull: 'Each 750mL bottle = 15 servings of 50mL pours.',
      info: `The High Rise Citrus Infusion is a citrus-forward, non-alcoholic pour designed for people who enjoy the ritual of a great drink, without the alcohol. Lime-forward, vibrant, refreshing — made with real fruit and organic cane sugar. Bright lift, smooth finish.`,
      roast: 'Lime · Bright',
      strengths: ['5', '10'],
      defaultStrength: '10',
      themeColor: '#89A84F',
      images: {
        '5':  { front: 'landing-citrus-front-5mg.png',  lifestyle: 'landing-citrus-lifestyle.png', back: 'landing-citrus-two-bottles.png', detail: 'landing-citrus-back.png' },
        '10': { front: 'landing-citrus-front-10mg.png', lifestyle: 'landing-citrus-lifestyle.png', back: 'landing-citrus-two-bottles.png', detail: 'landing-citrus-back.png' },
      },
    },
    orange: {
      title: 'Orange',
      eyebrow: 'Botanical Infusion · 750mL',
      tagline: 'Sun-kissed, juicy, with a soft creamy finish — 15 servings of 50mL pours.',
      tagline2: 'Bright. Creamy. Sunlit. Made for the moment.',
      pull: 'Bold enough to anchor a build, clean enough to enjoy over ice.',
      info: `The High Rise Orange Infusion captures the warmth of a sun-kissed orange. Bright, expressive, layered with natural sweetness. Crafted with real fruit and organic cane sugar — vibrant citrus character with a soft, creamy finish.`,
      roast: 'Orange · Creamy',
      strengths: ['10'],
      defaultStrength: '10',
      themeColor: '#F58B6F',
      images: {
        '10': { front: 'landing-orange-front.png', lifestyle: 'landing-orange-lifestyle.png', back: 'landing-orange-horizontal.jpg', detail: 'landing-orange-vertical.jpg' },
      },
    },
    raspberry: {
      title: 'Raspberry',
      eyebrow: 'Botanical Infusion · 750mL',
      tagline: 'Lush, fruit-forward, vibrant yet refined — 15 servings of 50mL pours.',
      tagline2: 'Lush raspberry meets gentle sweetness — vibrant yet refined.',
      pull: 'Bright, playful, and crafted for connection.',
      info: `The High Rise Raspberry Infusion is a fresh, fruit-forward pour. Lush raspberry notes meet gentle natural sweetness — vibrant yet refined. Smooth, creamy finish.`,
      roast: 'Raspberry · Lush',
      strengths: ['10'],
      defaultStrength: '10',
      themeColor: '#DF4661',
      images: {
        '10': { front: 'landing-raspberry-front.png', lifestyle: 'landing-raspberry-lifestyle.png', back: 'landing-raspberry-horizontal.jpg', detail: 'landing-raspberry-blackout.jpg' },
      },
    },
  };

  /* ---- Asset URL helper ---- */
  // We can't run Liquid at runtime, but the asset URLs follow a known
  // Shopify CDN pattern. Easier: find one rendered asset on the page,
  // extract its base path, then we can build URLs for any landing-*.
  const ASSET_BASE = (() => {
    const probe = document.querySelector('#thumb-front')?.src;
    if (!probe) return '';
    return probe.replace(/landing-[^/]+$/, '');
  })();
  const assetUrl = (filename) => ASSET_BASE + filename;

  /* ---- Gallery swap ---- */
  const updateGallery = (flavor, strength) => {
    const f = COPY[flavor];
    const imgs = f.images[strength] || f.images[f.defaultStrength] || Object.values(f.images)[0];
    if (!imgs) return;
    ['front', 'lifestyle', 'back', 'detail'].forEach((slot) => {
      const el = document.getElementById('thumb-' + slot);
      if (el && imgs[slot]) {
        const u = assetUrl(imgs[slot]);
        el.src = u;
      }
    });
    const activeSlot = $('#gallery-main')?.getAttribute('data-active-slot') || 'front';
    const main = document.getElementById('gal-main-img');
    if (main && imgs[activeSlot]) {
      main.src = assetUrl(imgs[activeSlot]);
      main.alt = `High Rise ${f.title} 750mL bottle`;
    }
  };

  /* ---- Strength pills availability ---- */
  const setStrengthAvailability = (flavor) => {
    const f = COPY[flavor];
    const variants = DATA[flavor]?.variants || {};
    let firstAvailable = null;
    $$('.size__pills label').forEach((lab) => {
      const sk = lab.getAttribute('data-strength');
      const inp = lab.querySelector('input');
      const available = !!variants[sk];
      lab.setAttribute('aria-disabled', String(!available));
      if (inp) {
        inp.disabled = !available;
        if (available && !firstAvailable) firstAvailable = inp;
      }
    });
    const checked = $$('input[name="strength"]').find((i) => i.checked && !i.disabled);
    if (!checked && firstAvailable) firstAvailable.checked = true;
    currentStrength = ($$('input[name="strength"]').find((i) => i.checked)?.dataset.strengthKey) || f.defaultStrength;
    const note = $('#strength-note');
    if (note) note.textContent = f.strengths.length === 1
      ? `Available at ${f.strengths[0]}mg for this flavor.`
      : 'Two strengths available for this flavor.';
  };

  /* ---- Price + purchase card refresh ---- */
  const refreshPrices = () => {
    const variant = DATA[currentFlavor]?.variants?.[currentStrength];
    if (!variant) return;
    const isSub = $$('input[name="purchase"]').find((i) => i.checked)?.value === 'subscribe';
    const sub = variant.sellingPlans?.[0];
    const onetimeEl = $('[data-onetime-price]');
    const subEl = $('[data-sub-price]');
    const strikeEl = $('[data-strike-price]');
    if (onetimeEl) onetimeEl.textContent = variant.priceFormatted;
    if (sub) {
      if (subEl) subEl.textContent = sub.priceFormatted;
      if (strikeEl) strikeEl.textContent = variant.priceFormatted;
    }
    $$('.purchase-card').forEach((card) => {
      const inp = card.querySelector('input');
      card.setAttribute('data-active', String(!!inp?.checked));
    });
  };

  /* ---- Flavor swap (full content refresh) ---- */
  const swap = (flavor) => {
    if (!COPY[flavor]) return;
    currentFlavor = flavor;
    const f = COPY[flavor];

    root.setAttribute('data-product', flavor);

    $$('.flavor-tile').forEach((t) => {
      const me = t.dataset.handle === flavor;
      t.classList.toggle('is-active', me);
      t.setAttribute('aria-selected', String(me));
    });

    setText('prod-title', f.title);
    setText('prod-title-m', f.title);
    setText('prod-eyebrow', f.eyebrow);
    setText('prod-tagline', f.tagline);
    setText('prod-tagline-mobile', f.tagline);

    setText('info-title', f.title);
    setText('info-tagline', f.tagline2 || f.tagline);
    setHTML('info-copy', f.info);
    setText('info-pull', f.pull);
    setText('spec-roast', f.roast);

    setStrengthAvailability(flavor);
    updateGallery(flavor, currentStrength);
    refreshPrices();

    history.replaceState(null, '', `?p=${flavor}`);
  };

  /* ---- Event wiring ---- */
  $$('.flavor-tile').forEach((t) => {
    t.addEventListener('click', (e) => {
      e.preventDefault();
      swap(t.dataset.handle);
    });
  });

  $$('input[name="strength"]').forEach((i) => {
    i.addEventListener('change', () => {
      currentStrength = i.dataset.strengthKey || '10';
      updateGallery(currentFlavor, currentStrength);
      refreshPrices();
    });
  });

  // Purchase mode (One-Time vs Stay Stocked). The .purchase-card IS a
  // <label> wrapping the radio, so clicking it natively toggles the
  // radio. We just listen to the change event — no click handler on
  // the label (that caused a race that re-toggled the radio off again).
  $$('input[name="purchase"]').forEach((i) => {
    i.addEventListener('change', refreshPrices);
  });

  $$('.gallery-thumb').forEach((b) => {
    b.addEventListener('click', () => {
      const slot = b.dataset.slot;
      $$('.gallery-thumb').forEach((x) => x.classList.toggle('is-active', x === b));
      const f = COPY[currentFlavor];
      const imgs = f.images[currentStrength] || f.images[f.defaultStrength];
      const main = document.getElementById('gal-main-img');
      if (main && imgs[slot]) {
        $('#gallery-main')?.setAttribute('data-active-slot', slot);
        main.src = assetUrl(imgs[slot]);
      }
    });
  });

  $$('[data-qty-dir]').forEach((b) => {
    b.addEventListener('click', () => {
      const inp = $('#qty-input');
      if (!inp) return;
      const dir = Number(b.dataset.qtyDir);
      inp.value = Math.max(1, Math.min(99, Number(inp.value) + dir));
    });
  });

  /* ---- ATC submit → hit Shopify /cart/add.js ---- */
  const form = $('#product-form');
  const atcBtn = $('#hr-atc-btn');
  form?.addEventListener('submit', async (e) => {
    e.preventDefault();
    if (atcBtn.getAttribute('aria-busy') === 'true') return;

    const variant = DATA[currentFlavor]?.variants?.[currentStrength];
    if (!variant) return;
    const qty = Math.max(1, Number($('#qty-input')?.value || 1));
    const isSub = $$('input[name="purchase"]').find((i) => i.checked)?.value === 'subscribe';

    let sellingPlanId = null;
    if (isSub) {
      const freq = $('#freq-select')?.value;
      // Word-boundary regex so "2 week" doesn't also match "12 weeks" if
      // Highrise ever adds a longer cadence. Today plans are
      // "Ships every 2|4|8 weeks" so the simple matcher works, but this
      // is one Shopify admin click away from being wrong.
      const freqRe = new RegExp(`\\b${freq}\\s*weeks?\\b`, 'i');
      const plan = (variant.sellingPlans || []).find((sp) => freqRe.test(sp.name)) || variant.sellingPlans?.[0];
      if (plan) sellingPlanId = plan.id;
    }

    atcBtn.setAttribute('aria-busy', 'true');
    atcBtn.setAttribute('disabled', '');

    try {
      const helper = root.__hr?.addToCart || (async ({variantId, quantity, sellingPlanId}) => {
        const body = { items: [{ id: variantId, quantity, ...(sellingPlanId ? { selling_plan: sellingPlanId } : {}) }] };
        const r = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify(body),
        });
        if (!r.ok) throw new Error((await r.json()).description || 'Add failed');
        const data = await r.json();
        window.publish?.((window.PUB_SUB_EVENTS || {}).cartUpdate || 'cart-update', {
          source: 'hr-bottles', cartData: data,
        });
        return data;
      });
      await helper({
        variantId: variant.variantId,
        quantity: qty,
        sellingPlanId,
      });
      root.__hr?.showToast?.('Added to cart', 'success');
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        setTimeout(() => drawer.open(atcBtn), 280);
      }
    } catch (err) {
      root.__hr?.showToast?.(err.message || 'Could not add to cart', 'error');
    } finally {
      atcBtn.removeAttribute('aria-busy');
      atcBtn.removeAttribute('disabled');
    }
  });

  /* ---- Init ---- */
  swap(currentFlavor);
})();
